import React, { useState, useEffect, useMemo } from "react";
import { View, Text, Pressable, StyleSheet, Platform, Clipboard } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import { useUser } from "./UserContext";
import bundledVersionInfo from "../version.json";
import { stripeConfigService } from "../services/StripeConfigService";

// ─── Admin access ─────────────────────────────────────────────────────────────
const ADMIN_EMAIL_WHITELIST = [
  'trevorhawkins@dsignable.com',
  'info@mytassel.com',
];

// ─── Environment detection ───────────────────────────────────────────────────
const detectEnvironment = () => {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return 'native';
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return 'development';
  if (host.includes('staging') || host.includes('mytassel-staging')) return 'staging';
  return 'production';
};

const ENV_COLORS = {
  development: { bg: '#6366f1', label: 'DEV' },
  staging:     { bg: '#f59e0b', label: 'STAGING' },
  production:  { bg: '#10b981', label: 'PROD' },
  native:      { bg: '#64748b', label: 'NATIVE' },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZoneName: 'short',
  });
};

const copyToClipboard = (text) => {
  if (Platform.OS === 'web' && navigator?.clipboard) {
    navigator.clipboard.writeText(text).catch(() => {});
  } else {
    Clipboard.setString(text);
  }
};

// ─── Component ───────────────────────────────────────────────────────────────
const VersionBadge = () => {
  const { currentUserData } = useUser();
  const [expanded, setExpanded] = useState(false);
  const [remoteVersion, setRemoteVersion] = useState(null);
  const [firestoreVersion, setFirestoreVersion] = useState(null);
  const [productionVersion, setProductionVersion] = useState(null);
  const [copiedCmd, setCopiedCmd] = useState(null);

  const environment = useMemo(detectEnvironment, []);
  const envStyle = ENV_COLORS[environment] || ENV_COLORS.production;
  const [stripeMode, setStripeMode] = useState(null);

  // Live version info: Firestore wins, falls back to bundled version.json
  const versionInfo = firestoreVersion || bundledVersionInfo;

  // ── Admin gate ──────────────────────────────────────────────────────────────
  const isAdmin = useMemo(() => {
    if (!currentUserData) return false;
    const email = currentUserData.email?.toLowerCase();
    return currentUserData.accountType === 'admin' || ADMIN_EMAIL_WHITELIST.includes(email);
  }, [currentUserData]);

  // ── Fetch live version from Firestore (config/appVersion) ───────────────────
  useEffect(() => {
    if (!isAdmin) return;
    const db = getFirestore();
    getDoc(doc(db, 'config', 'appVersion'))
      .then(snap => { if (snap.exists()) setFirestoreVersion(snap.data()); })
      .catch(() => {});
  }, [isAdmin]);

  // ── Fetch deployed app-version.json for stale-bundle detection ──────────────
  useEffect(() => {
    if (!isAdmin || Platform.OS !== 'web') return;
    fetch(`${window.location.origin}/app-version.json?_=${Date.now()}`)
      .then(r => r.json())
      .then(setRemoteVersion)
      .catch(() => {});
  }, [isAdmin]);

  // ── Fetch production version from production host ────────────────────────────
  useEffect(() => {
    if (!isAdmin || Platform.OS !== 'web') return;
    if (environment === 'development') return;
    fetch(`https://mytassel.com/app-version.json?_=${Date.now()}`)
      .then(r => r.json())
      .then(setProductionVersion)
      .catch(() => {});
  }, [isAdmin, environment]);

  useEffect(() => {
    if (!isAdmin) return;
    stripeConfigService.getStripeMode().then(setStripeMode).catch(() => {});
    const cleanup = stripeConfigService.addChangeListener(() => {
      stripeConfigService.getStripeMode().then(setStripeMode).catch(() => {});
    });
    return cleanup;
  }, [isAdmin]);

  const stripeIsLive = stripeMode === 'live';
  const stripeEnvLocked = stripeConfigService.isEnvironmentLocked();

  // Bundle vs deployed on this env — green = in sync, red = stale
  const envDeployedVersion = remoteVersion?.version;
  const bundleMatchesEnv = !envDeployedVersion || envDeployedVersion === bundledVersionInfo.version;

  // ── Copy command helper ─────────────────────────────────────────────────────
  const handleCopy = (cmd) => {
    copyToClipboard(cmd);
    setCopiedCmd(cmd);
    setTimeout(() => setCopiedCmd(null), 2000);
  };

  if (!isAdmin) return null;

  const COMMANDS = [
    { label: 'Bump patch',      cmd: 'npm run version:patch',      icon: 'git-commit-outline' },
    { label: 'Bump minor',      cmd: 'npm run version:minor',      icon: 'trending-up-outline' },
    { label: 'Bump major',      cmd: 'npm run version:major',      icon: 'rocket-outline' },
    { label: 'Deploy staging',  cmd: 'npm run deploy:staging',     icon: 'cloud-upload-outline' },
    { label: 'Deploy prod',     cmd: 'npm run deploy:production',  icon: 'checkmark-circle-outline' },
    { label: 'Deploy both',     cmd: 'npm run deploy:both',        icon: 'layers-outline' },
  ];

  return (
    <View style={styles.wrapper} pointerEvents="box-none">
      {/* ── Expanded panel ── */}
      {expanded && (
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>App Status</Text>

          {/* Version rows */}
          <View style={styles.row}>
            <Text style={styles.rowLabel}>Bundle version</Text>
            <Text style={[styles.rowValue, { color: bundleMatchesEnv ? '#4ade80' : '#f87171' }]}>
              v{bundledVersionInfo.version} {bundleMatchesEnv ? '✓' : '⚠ stale'}
            </Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.rowLabel}>{envStyle.label} version</Text>
            <Text style={[styles.rowValue, { color: envStyle.bg }]}>
              v{envDeployedVersion || '…'}
            </Text>
          </View>

          {productionVersion && environment !== 'production' && (
            <View style={styles.row}>
              <Text style={styles.rowLabel}>Production version</Text>
              <Text style={[styles.rowValue, { color: ENV_COLORS.production.bg }]}>
                v{productionVersion.version}
              </Text>
            </View>
          )}

          <View style={styles.row}>
            <Text style={styles.rowLabel}>Build #</Text>
            <Text style={styles.rowValue}>#{versionInfo.buildNumber}</Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.rowLabel}>Git commit</Text>
            <Text style={[styles.rowValue, styles.mono]}>{versionInfo.gitCommit || '—'}</Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.rowLabel}>Environment</Text>
            <View style={[styles.envPill, { backgroundColor: envStyle.bg }]}>
              <Text style={styles.envPillText}>{envStyle.label}</Text>
            </View>
          </View>

          {stripeMode && (
            <View style={styles.row}>
              <Text style={styles.rowLabel}>Stripe mode</Text>
              <View style={styles.stripeRow}>
                <View style={[
                  styles.envPill,
                  stripeIsLive
                    ? (Platform.OS === 'web' ? { background: 'linear-gradient(135deg, #6366f1, #a855f7)' } : { backgroundColor: '#7c3aed' })
                    : { backgroundColor: '#f59e0b' }
                ]}>
                  <Text style={styles.envPillText}>{stripeMode.toUpperCase()}</Text>
                </View>
                {stripeEnvLocked && (
                  <Ionicons name="lock-closed" size={10} color="#64748b" style={{ marginLeft: 4 }} />
                )}
              </View>
            </View>
          )}

          <View style={styles.divider} />

          <View style={styles.row}>
            <Text style={styles.rowLabel}>Last staging deploy</Text>
            <Text style={[styles.rowValue, styles.dateValue]}>
              {formatDate(versionInfo.lastStagingDeploy)}
            </Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.rowLabel}>Last production deploy</Text>
            <Text style={[styles.rowValue, styles.dateValue]}>
              {formatDate(versionInfo.lastProductionDeploy)}
            </Text>
          </View>

          {versionInfo.releaseNotes ? (
            <>
              <View style={styles.divider} />
              <Text style={styles.rowLabel}>Release notes</Text>
              <Text style={styles.notes}>{versionInfo.releaseNotes}</Text>
            </>
          ) : null}

          <View style={styles.divider} />

          {/* Commands */}
          <Text style={styles.sectionTitle}>Commands</Text>
          <Text style={styles.commandsHint}>Tap to copy, run in terminal</Text>
          <View style={styles.commandsGrid}>
            {COMMANDS.map(({ label, cmd, icon }) => (
              <Pressable
                key={cmd}
                style={[styles.cmdBtn, copiedCmd === cmd && styles.cmdBtnCopied]}
                onPress={() => handleCopy(cmd)}
              >
                <Ionicons
                  name={copiedCmd === cmd ? 'checkmark-outline' : icon}
                  size={13}
                  color={copiedCmd === cmd ? '#4ade80' : '#94a3b8'}
                  style={{ marginRight: 5 }}
                />
                <Text style={[styles.cmdLabel, copiedCmd === cmd && { color: '#4ade80' }]}>
                  {copiedCmd === cmd ? 'Copied!' : label}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>
      )}

      {/* ── Collapsed pill / toggle ── */}
      <Pressable style={styles.pill} onPress={() => setExpanded(e => !e)}>
        <View style={[styles.envDot, { backgroundColor: bundleMatchesEnv ? '#4ade80' : '#f87171' }]} />
        <Text style={[styles.pillVersion, { color: bundleMatchesEnv ? '#4ade80' : '#f87171' }]}>
          v{envDeployedVersion || bundledVersionInfo.version}
        </Text>
        <Text style={styles.pillEnv}>{envStyle.label}</Text>
        {stripeMode && (
          <>
            <View style={styles.pillDivider} />
            {stripeIsLive ? (
              Platform.OS === 'web'
                ? <View style={[styles.stripeDot, { background: 'linear-gradient(135deg, #6366f1, #a855f7)', borderRadius: 3 }]} />
                : <View style={[styles.stripeDot, { backgroundColor: '#7c3aed' }]} />
            ) : (
              <View style={[styles.stripeDot, { backgroundColor: '#f59e0b' }]} />
            )}
            <Text style={[styles.pillStripe, { color: stripeIsLive ? '#a78bfa' : '#fcd34d' }]}>
              {stripeIsLive ? 'LIVE' : 'TEST'}
            </Text>
          </>
        )}
        <Ionicons
          name={expanded ? 'chevron-down-outline' : 'chevron-up-outline'}
          size={12}
          color="#64748b"
          style={{ marginLeft: 4 }}
        />
      </Pressable>
    </View>
  );
};

export default VersionBadge;

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  wrapper: {
    position: 'fixed',
    bottom: 16,
    left: 16,
    zIndex: 99999,
    alignItems: 'flex-start',
  },
  // Collapsed pill
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(15, 23, 42, 0.85)',
    borderRadius: 20,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    ...(Platform.OS === 'web' ? { backdropFilter: 'blur(8px)', cursor: 'pointer' } : {}),
  },
  envDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginRight: 6,
  },
  pillVersion: {
    color: '#e2e8f0',
    fontSize: 11,
    fontWeight: '600',
    marginRight: 5,
  },
  pillEnv: {
    color: '#64748b',
    fontSize: 10,
    fontWeight: '500',
  },
  pillDivider: {
    width: 1,
    height: 10,
    backgroundColor: 'rgba(255,255,255,0.15)',
    marginHorizontal: 6,
  },
  stripeDot: {
    width: 5,
    height: 5,
    borderRadius: 3,
    marginRight: 4,
  },
  pillStripe: {
    fontSize: 9,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  staleDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#fbbf24',
    marginLeft: 5,
  },
  // Expanded panel
  panel: {
    backgroundColor: 'rgba(15, 23, 42, 0.95)',
    borderRadius: 12,
    padding: 14,
    marginBottom: 8,
    width: 320,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    ...(Platform.OS === 'web' ? { backdropFilter: 'blur(12px)' } : {}),
  },
  panelTitle: {
    color: '#f1f5f9',
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 10,
    letterSpacing: 0.5,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  rowLabel: {
    color: '#64748b',
    fontSize: 11,
    flex: 1,
  },
  rowValue: {
    color: '#cbd5e1',
    fontSize: 11,
    fontWeight: '600',
    textAlign: 'right',
    flex: 2,
  },
  mono: {
    fontFamily: Platform.OS === 'web' ? 'monospace' : 'Courier',
    letterSpacing: 0.5,
  },
  dateValue: {
    fontSize: 10,
    fontWeight: '500',
  },
  stripeRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  envPill: {
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  envPillText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  divider: {
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.07)',
    marginVertical: 8,
  },
  sectionTitle: {
    color: '#f1f5f9',
    fontSize: 11,
    fontWeight: '700',
    marginBottom: 2,
    letterSpacing: 0.5,
  },
  commandsHint: {
    color: '#475569',
    fontSize: 10,
    marginBottom: 8,
  },
  commandsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  cmdBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  cmdBtnCopied: {
    borderColor: 'rgba(74, 222, 128, 0.3)',
    backgroundColor: 'rgba(74, 222, 128, 0.06)',
  },
  cmdLabel: {
    color: '#94a3b8',
    fontSize: 10,
    fontWeight: '500',
  },
  notes: {
    color: '#94a3b8',
    fontSize: 10,
    marginTop: 4,
    lineHeight: 15,
  },
});
